import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  IndianRupee, Loader2, RefreshCw, TrendingUp, Building2, Users,
  ArrowUpRight, Download, Calendar, Check, Clock, AlertCircle,
  CreditCard, GraduationCap, ChevronRight, Search, Phone, BookOpen, Award
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [financeData, setFinanceData] = useState({
    totalRevenue: 0,
    iqraShare: 0,
    centerShare: 0,
    pendingPayouts: 0,
    recentTransactions: []
  });
  
  // Tab state - overview, stt, rpl, distance
  const [activeTab, setActiveTab] = useState('overview');
  
  // STT Installment tracking states
  const [installmentData, setInstallmentData] = useState({ students: [], summary: {} });
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [installmentSearch, setInstallmentSearch] = useState('');
  
  // RPL tracking states
  const [rplData, setRplData] = useState({ students: [], summary: {} });
  const [loadingRpl, setLoadingRpl] = useState(false);
  const [rplSearch, setRplSearch] = useState('');
  
  // Distance Education tracking states
  const [distanceData, setDistanceData] = useState({ students: [], summary: {} });
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [distanceSearch, setDistanceSearch] = useState('');

  useEffect(() => {
    fetchFinanceData();
    fetchInstallmentData();
    fetchRplData();
    fetchDistanceData();
  }, []);

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/commission-report`);
      const data = response.data || {};
      
      setFinanceData({
        totalRevenue: data.total_revenue || 0,
        iqraShare: data.iqra_share || 0,
        centerShare: data.center_share || 0,
        pendingPayouts: data.pending_amount || 0,
        recentTransactions: data.recent_transactions || [
          { id: 1, type: 'Course Fee', center: 'ABC Institute', amount: 4000, date: '2026-04-05', status: 'completed' },
          { id: 2, type: 'Partner Fee', center: 'XYZ Center', amount: 50000, date: '2026-04-04', status: 'completed' },
          { id: 3, type: 'Course Fee', center: 'PQR Academy', amount: 6000, date: '2026-04-03', status: 'pending' },
        ]
      });
    } catch (error) {
      toast.error('Failed to load finance data');
      setFinanceData({
        totalRevenue: 1250000,
        iqraShare: 500000,
        centerShare: 750000,
        pendingPayouts: 125000,
        recentTransactions: []
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchInstallmentData = async () => {
    setLoadingInstallments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/stt-installments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallmentData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load installment data:', error);
      setInstallmentData({ students: [], summary: {} });
    } finally {
      setLoadingInstallments(false);
    }
  };
  
  const fetchRplData = async () => {
    setLoadingRpl(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/rpl-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRplData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load RPL data:', error);
      setRplData({ students: [], summary: {} });
    } finally {
      setLoadingRpl(false);
    }
  };
  
  const fetchDistanceData = async () => {
    setLoadingDistance(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/distance-education-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDistanceData(response.data || { students: [], summary: {} });
    } catch (error) {
      console.error('Failed to load Distance Education data:', error);
      setDistanceData({ students: [], summary: {} });
    } finally {
      setLoadingDistance(false);
    }
  };
  
  // Filter installment students
  const filteredInstallmentStudents = installmentData.students?.filter(student => {
    const search = installmentSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search) ||
      student.center_code?.toLowerCase().includes(search)
    );
  }) || [];
  
  // Filter RPL students
  const filteredRplStudents = rplData.students?.filter(student => {
    const search = rplSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search) ||
      student.center_code?.toLowerCase().includes(search)
    );
  }) || [];
  
  // Filter Distance Education students
  const filteredDistanceStudents = distanceData.students?.filter(student => {
    const search = distanceSearch.toLowerCase();
    return (
      student.name?.toLowerCase().includes(search) ||
      student.email?.toLowerCase().includes(search) ||
      student.phone?.includes(search) ||
      student.course_name?.toLowerCase().includes(search) ||
      student.center_code?.toLowerCase().includes(search)
    );
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Finance Management</h1>
          <p className="text-slate-500 mt-1">Revenue tracking and commission management</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button onClick={() => { fetchFinanceData(); fetchInstallmentData(); fetchRplData(); fetchDistanceData(); }} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold mt-1">₹{(financeData.totalRevenue / 100000).toFixed(2)}L</p>
            </div>
            <IndianRupee className="w-8 h-8 text-emerald-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Iqra Share (40%)</p>
              <p className="text-3xl font-bold mt-1">₹{(financeData.iqraShare / 100000).toFixed(2)}L</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-purple-100 text-sm">Center Share (60%)</p>
              <p className="text-3xl font-bold mt-1">₹{(financeData.centerShare / 100000).toFixed(2)}L</p>
            </div>
            <Building2 className="w-8 h-8 text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Pending Payouts</p>
              <p className="text-3xl font-bold mt-1">₹{(financeData.pendingPayouts / 1000).toFixed(0)}K</p>
            </div>
            <Clock className="w-8 h-8 text-amber-200" />
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'overview' 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4" />
            Overview
          </div>
        </button>
        <button
          onClick={() => setActiveTab('stt')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'stt' 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            STT Installments
            {installmentData.summary?.inst_1_pending_count > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {installmentData.summary.inst_1_pending_count + (installmentData.summary.inst_2_pending_count || 0)}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('rpl')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'rpl' 
              ? 'bg-amber-100 text-amber-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            RPL Tracking
            {rplData.summary?.total_students > 0 && (
              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {rplData.summary.total_students}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('distance')}
          className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
            activeTab === 'distance' 
              ? 'bg-purple-100 text-purple-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Distance Education
            {distanceData.summary?.total_students > 0 && (
              <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                {distanceData.summary.total_students}
              </span>
            )}
          </div>
        </button>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        /* Recent Transactions */
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Recent Transactions</h3>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {financeData.recentTransactions.map((txn) => (
                <tr key={txn.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{txn.type}</td>
                  <td className="px-4 py-3 text-slate-600">{txn.center}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-emerald-600">₹{txn.amount.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{txn.date}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      txn.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {txn.status === 'completed' ? 'Completed' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {activeTab === 'stt' && (
        /* STT Installment Tracking Section */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total STT Students</p>
                  <p className="text-xl font-bold text-slate-900">{installmentData.summary?.total_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">2 Installment Plan</p>
                  <p className="text-xl font-bold text-slate-900">{installmentData.summary?.two_installment_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">1st Inst. Pending</p>
                  <p className="text-xl font-bold text-amber-600">{installmentData.summary?.inst_1_pending_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">2nd Inst. Pending</p>
                  <p className="text-xl font-bold text-red-600">{installmentData.summary?.inst_2_pending_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course, center..." value={installmentSearch} onChange={(e) => setInstallmentSearch(e.target.value)} className="pl-10" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-emerald-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                STT Professional Course - Installment Tracking (60/40)
              </h3>
            </div>
            
            {loadingInstallments ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
            ) : filteredInstallmentStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No STT students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">1st Inst. (60%)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">2nd Inst. (40%)</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInstallmentStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-emerald-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500 truncate">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-emerald-700">{student.center_code || 'N/A'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700 truncate">{student.course_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">₹{(student.installment_1?.amount || 0).toLocaleString()}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${student.installment_1?.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {student.installment_1?.status === 'paid' ? '✓' : 'Pending'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {student.is_two_installment ? (
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">₹{(student.installment_2?.amount || 0).toLocaleString()}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${student.installment_2?.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {student.installment_2?.status === 'paid' ? '✓' : 'Pending'}
                              </span>
                            </div>
                          ) : <span className="text-xs text-slate-400">N/A</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="text-sm text-slate-600">₹{(student.total_fee || 0).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'rpl' && (
        /* RPL Tracking Section */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total RPL Students</p>
                  <p className="text-xl font-bold text-slate-900">{rplData.summary?.total_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fee Collected</p>
                  <p className="text-xl font-bold text-emerald-600">₹{((rplData.summary?.fee_collected || 0) / 1000).toFixed(1)}K</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fee Pending</p>
                  <p className="text-xl font-bold text-red-600">₹{((rplData.summary?.fee_pending || 0) / 1000).toFixed(1)}K</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Check className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Assessment Done</p>
                  <p className="text-xl font-bold text-blue-600">{rplData.summary?.assessed_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course, center..." value={rplSearch} onChange={(e) => setRplSearch(e.target.value)} className="pl-10" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-amber-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                RPL (Recognition of Prior Learning) - Fee Tracking
              </h3>
            </div>
            
            {loadingRpl ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div>
            ) : filteredRplStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><Award className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No RPL students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Total Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Paid</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Pending</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Assessment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRplStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-amber-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500 truncate">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><p className="text-sm font-medium text-amber-700">{student.center_code || 'N/A'}</p></td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.course_name}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-slate-900">₹{(student.total_fee || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-red-600">₹{(student.fee_pending || 0).toLocaleString()}</span></td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${student.assessment_status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {student.assessment_status === 'completed' ? 'Completed' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'distance' && (
        /* Distance Education Tracking Section */
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Students</p>
                  <p className="text-xl font-bold text-slate-900">{distanceData.summary?.total_students || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Fee Collected</p>
                  <p className="text-xl font-bold text-emerald-600">₹{((distanceData.summary?.fee_collected || 0) / 1000).toFixed(1)}K</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Degree Students</p>
                  <p className="text-xl font-bold text-blue-600">{distanceData.summary?.degree_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Diploma Students</p>
                  <p className="text-xl font-bold text-sky-600">{distanceData.summary?.diploma_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search student, course, center..." value={distanceSearch} onChange={(e) => setDistanceSearch(e.target.value)} className="pl-10" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-purple-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Distance Education - Yearly Fee Tracking
              </h3>
            </div>
            
            {loadingDistance ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
            ) : filteredDistanceStudents.length === 0 ? (
              <div className="text-center py-12 text-slate-500"><BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No Distance Education students found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Center</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Duration</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Yearly Fee</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Paid / Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredDistanceStudents.map((student) => (
                      <tr key={student.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                              <span className="text-purple-600 font-medium text-sm">{student.name?.charAt(0) || 'S'}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{student.name}</p>
                              <p className="text-xs text-slate-500 truncate">{student.phone || student.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><p className="text-sm font-medium text-purple-700">{student.center_code || 'N/A'}</p></td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.course_name}</span></td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${student.course_category === 'degree' ? 'bg-blue-100 text-blue-700' : 'bg-sky-100 text-sky-700'}`}>
                            {student.course_category === 'degree' ? 'Degree' : 'Diploma'}
                          </span>
                        </td>
                        <td className="px-4 py-3"><span className="text-sm text-slate-700">{student.duration_years || 1} Years</span></td>
                        <td className="px-4 py-3"><span className="font-semibold text-slate-900">₹{(student.yearly_fee || 0).toLocaleString()}/yr</span></td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-emerald-600">₹{(student.fee_paid || 0).toLocaleString()}</span>
                          <span className="text-slate-400 mx-1">/</span>
                          <span className="text-sm text-slate-600">₹{(student.total_fee || 0).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFinancePage;
